import React from 'react';
import { z } from 'zod';
import { useHistory } from 'react-router-dom';
import { InputField } from 'components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button, IconButton, Stack, Tooltip, Box } from '@mui/material';
import { Add, DeleteOutline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { Department } from '../../../types';
import { useBPFDepartments } from '../../../api/departments';

const departmentsFormValueSchema = z.object({
  departments: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().min(3).max(100),
    })
  ),
});

type DepartmentsFormValue = z.infer<typeof departmentsFormValueSchema>;

export const DepartmentsForm = ({ list }: { list: Department[] }) => {
  const { goBack } = useHistory();
  const { isUpserting, handleUpsertDepartments } = useBPFDepartments();

  const { control, handleSubmit } = useForm<DepartmentsFormValue>({
    resolver: zodResolver(departmentsFormValueSchema),
    defaultValues: { departments: list ?? [] },
  });

  const { fields, move, append, remove } = useFieldArray({ control, name: 'departments' });

  const handleSave = async (formData: DepartmentsFormValue) => {
    const dto = formData.departments.map((department, index) => ({
      ordinal: index,
      name: department.name,
      id: department?.id ?? null,
    }));

    await handleUpsertDepartments(dto);
  };

  return (
    <Stack gap="1rem" height="100%">
      <Stack gap="1rem" flex={1}>
        {fields.map(({ id }, index, rows) => (
          <Stack key={id} direction="row" gap="0.5rem" padding="0.5rem">
            <Tooltip title="Move row down">
              <IconButton size="small" disabled={index + 1 > rows.length - 1} onClick={() => move(index, index + 1)}>
                <KeyboardArrowDown />
              </IconButton>
            </Tooltip>

            <Tooltip title="Move row up">
              <IconButton size="small" disabled={!index} onClick={() => move(index, index - 1)}>
                <KeyboardArrowUp />
              </IconButton>
            </Tooltip>

            <Tooltip title="Remove department">
              <IconButton size="small" color="error" onClick={() => remove(index)}>
                <DeleteOutline />
              </IconButton>
            </Tooltip>

            <InputField
              label="Name"
              control={control}
              name={`departments.${index}.name`}
              inputProps={{ variant: 'outlined' }}
            />
          </Stack>
        ))}

        <Box>
          <Button
            size="large"
            variant="outlined"
            startIcon={<Add />}
            disabled={isUpserting}
            onClick={() => append({ name: '' })}
          >
            Add Department
          </Button>
        </Box>
      </Stack>

      <Stack direction="row" gap="0.5rem">
        <Button loading={isUpserting} variant="contained" onClick={handleSubmit(handleSave)}>
          Save
        </Button>

        <Button disabled={isUpserting} onClick={goBack}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
